import React, { createContext, useContext, useState, useEffect } from 'react';

const TranslationContext = createContext();

// Hoisted to module scope (out of the component) so it's available to the
// useState lazy initializer below without being recreated every render, and so
// both the initializer and the init effect's later poll share one single source
// of truth for the code->display-name mapping.
const LANGUAGE_DISPLAY_MAP = {
    'en-US': 'English',
    'es-US': 'Español',
    'ur-PK': 'Русский',
    'ta-IN': 'हिंदी',
    'ar-SA': 'العربية',
    'ru-RU': 'Русский',
    'zh-CN': '中文',
    'ht-HT': 'Kreyòl Ayisyen'
};
const languageMap = {
        'English': 'en-US',
        'Español': 'es-US',
        'العربية': 'ar-SA',
        'हिंदी': 'hi-IN',
        'Русский': 'ru-RU',
        '中文': 'zh-CN',
        'Kreyòl Ayisyen': 'ht-HT'
    };

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within TranslationProvider');
    }
    return context;
};

export const TranslationProvider = ({ children }) => {
    // Initialize synchronously from whatever language was saved last session (lazy
    // initializer) instead of always defaulting to 'English' and silently correcting
    // itself later once the init effect's poll below catches up. Previously the dropdown
    // (and any page rendered/navigated to in the meantime) would visibly show/apply
    // "English" for however long it took Akorbi's script to load — up to 10 SECONDS, since
    // the poll only checked every 10000ms — before abruptly flipping to the actually-saved
    // language once that check finally fired. That multi-second "still says English" window
    // was long enough to comfortably navigate all the way to the Results page, making it
    // look like Results itself was randomly/wrongly swapping to Spanish out of nowhere even
    // though the user's real saved preference was Spanish the entire time.
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        // This initializer runs once per full page load (refresh or a new tab), not on
        // in-app navigation. Always reset the language to English here and mirror it in
        // both localStorage and sessionStorage so the init effect below (which reads
        // localStorage) restores English instead of a previously selected language.
        localStorage.setItem('ptLanguage', 'en-US');
        sessionStorage.setItem('ptLanguage', 'en-US');
        return LANGUAGE_DISPLAY_MAP['en-US'];
    });
    const [isTranslatorReady, setIsTranslatorReady] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    // Surfaces the shared Akorbi translation-unavailable message (see
    // reports/SPECIFIC_ERROR_MESSAGES_PLAN.md) as a transient, dismissible toast whenever
    // the widget fails to initialize or a language switch fails. Never blocks provider
    // search — the page always falls back to/stays in English, this is purely
    // informational. Auto-dismisses after a few seconds via the effect below.
    const [translationError, setTranslationError] = useState('');

    // Language mappings
    const languageMap = {
        'English': 'en-US',
        'Español': 'es-US',
        'العربية': 'ar-SA',
        'हिंदी': 'hi-IN',
        'Русский': 'ru-RU',
        '中文': 'zh-CN',
        'Kreyòl Ayisyen': 'ht-HT'
    };

    const languageDisplayMap = LANGUAGE_DISPLAY_MAP;

    // Initialize translation service when app loads
    useEffect(() => {
        console.log('🌐 Initializing translation service...');

        // Wait for Akorbi script to load. Poll frequently (every 200ms, not every 10
        // SECONDS as before) so there's only a brief, imperceptible delay between the
        // script becoming available and translation actually being applied/confirmed —
        // instead of a window of up to 10 seconds where the page could be fully navigated
        // (e.g. all the way from Search to Results) before the saved language ever took
        // effect.
        // `readyFlag` mirrors isTranslatorReady but is read/written synchronously via a
        // plain local variable instead of React state. The 10s timeout callback below
        // was previously checking the `isTranslatorReady` STATE value directly — but
        // since this effect has a `[]` dependency array, that closure only ever sees
        // isTranslatorReady's value from the initial render (always `false`), never the
        // updated value set inside the interval callback below. That stale closure meant
        // the timeout fired `setTranslationError(...)` unconditionally 10 seconds after
        // mount EVERY time, even when the translator loaded and became ready within the
        // first 200ms — i.e. the error was "kicking in" on every page load regardless of
        // whether translation actually succeeded. Reading/writing a local variable here
        // instead avoids the stale-closure problem entirely.
        let readyFlag = false;
        const checkInterval = setInterval(() => {
            try {
                if (window.crestStub && window.crestTranslator &&
                    typeof window.crestStub.setCurrentLanguage === 'function') {
                    clearInterval(checkInterval);
                    readyFlag = true;
                    console.log('✅ Akorbi translation service loaded and ready');
                    setIsTranslatorReady(true);

                    // Get saved language preference or default to English
                    const savedLanguage = localStorage.getItem('ptLanguage') || 'en-US';
                    console.log(`Current language preference: ${savedLanguage}`);
                    
                    // Update displayed language in dropdown
                    setSelectedLanguage(languageDisplayMap[savedLanguage] || 'English');

                    // DO NOT call setCurrentLanguage or start() here - causes infinite reloads
                    // The Akorbi script should handle language restoration automatically
                    window.crestStub.setCurrentLanguage(savedLanguage);
                    window.crestTranslator.start();
                }
            } catch (err) {
                clearInterval(checkInterval);
                console.warn('⚠️ Akorbi translation service threw an error during init — translation disabled:', err);
                setTranslationError('translation-init-error');
            }
        }, 200);

        // Timeout after 10 seconds
        const timeoutId = setTimeout(() => {
            clearInterval(checkInterval);
            if (!readyFlag) {
                console.warn('⚠️ Akorbi translation service not available — continuing without translation');
                setTranslationError('translation-init-error');
            }
        }, 10000);

        return () => {
            clearInterval(checkInterval);
            clearTimeout(timeoutId);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-dismiss the translation-error toast after a few seconds — it's purely
    // informational (English is already showing / still applied), so it shouldn't
    // linger on screen indefinitely like a blocking error would.
    useEffect(() => {
        if (!translationError) return;
        const dismissTimer = setTimeout(() => setTranslationError(''), 8000);
        return () => clearTimeout(dismissTimer);
    }, [translationError]);

    const handleLanguageChange = (language) => {
        const langCode = languageMap[language] || 'en-US';
        
        // Get current language to avoid unnecessary operations
        const currentLanguage = localStorage.getItem("ptLanguage") || 'en-US';
        
        // If same language is selected, don't do anything
        if (currentLanguage === langCode) {
            console.log(`ℹ️ Language already set to ${language} (${langCode})`);
            return;
        }

        try {
            // Check if crestStub is available
            if (window.crestStub && typeof window.crestStub.setCurrentLanguage === 'function') {
                console.log(`🌐 Changing language from ${currentLanguage} to ${langCode}`);
                
                // Update local state
                setSelectedLanguage(language);
                
                // Save language preference
                localStorage.setItem("ptLanguage", langCode);
                sessionStorage.setItem("ptLanguage", langCode);

                // The Akorbi/Crest widget only reliably translates the ORIGINAL (English)
                // DOM — it scans/replaces text nodes in place and has no concept of
                // "already-translated" content. Going straight from one non-English
                // language to another (e.g. Español -> Русский) asks it to translate
                // already-translated Spanish text directly into Russian, which it can't
                // do purely client-side; instead it falls back to forcing a hard
                // window.location.reload() itself to get back to a clean English DOM
                // before re-translating — which is the "page refreshing instead of
                // translating" behavior being fixed here.
                //
                // Fix: when leaving a non-English language, first silently restore the
                // DOM to English (setCurrentLanguage('en-US') + start()) and let that
                // settle, THEN apply the newly-selected language — mirroring the same
                // English -> target hop that already works fine, instead of ever asking
                // the widget to hop directly between two non-English languages. Only
                // needed when the CURRENT language isn't English; English -> anything
                // already goes through a single hop and doesn't need this.
                const needsEnglishResetFirst = currentLanguage !== 'en-US' && window.crestTranslator &&
                    typeof window.crestTranslator.start === 'function';

                console.log('🔄 Applying translation...');
                setIsTranslating(true); // show loader for the whole transition

                const applyTargetLanguage = () => {
                    window.crestStub.setCurrentLanguage(langCode);
                    if (window.crestTranslator && typeof window.crestTranslator.start === 'function') {
                        setTimeout(() => {
                            const startResult = window.crestTranslator.start();
                            console.log(`Translation applied: ${startResult ? 'Success' : 'Failed or already running'}`);
                            setIsTranslating(false); // hide loader
                        }, 3000);
                    } else {
                        setIsTranslating(false);
                    }
                };

                if (needsEnglishResetFirst) {
                    console.log('↩️ Resetting to English first before applying new language (avoids widget reload)');
                    window.crestStub.setCurrentLanguage('en-US');
                    window.crestTranslator.start();
                    setTimeout(applyTargetLanguage, 1500);
                } else {
                    applyTargetLanguage();
                }
                
            } else {
                console.warn('⚠️ Translation service not available');
                setTranslationError('translation-init-error');
                localStorage.setItem("ptLanguage", langCode);
                sessionStorage.setItem("ptLanguage", langCode);
                setSelectedLanguage(language);
            }
        } catch (error) {
            console.error('❌ Error changing language:', error);
            setTranslationError('translation-init-error');
            setIsTranslating(false); // ensure loader is hidden on error
        }
    };

    const value = {
        selectedLanguage,
        handleLanguageChange,
        isTranslatorReady,
        isTranslating,
        languageMap,
        languageDisplayMap,
        translationError,
        clearTranslationError: () => setTranslationError(''),
        // Re-apply the currently-selected language's translation to newly-rendered content
        // (e.g. an async-loaded results list) WITHOUT going through handleLanguageChange —
        // that function intentionally no-ops when the language hasn't changed (see the
        // "already set to X" early-return above), which is correct for the dropdown but
        // wrong here since we specifically need to re-translate when the language is
        // UNCHANGED but the DOM content is new. Reuses the same isTranslating overlay as
        // handleLanguageChange so this looks like the same deliberate, masked transition
        // instead of a jarring raw flash of English text briefly appearing before flipping
        // to the selected language.
        retranslateContent: () => {
            if (selectedLanguage === 'English') return;
            if (!window.crestTranslator || typeof window.crestTranslator.start !== 'function') return;
            setIsTranslating(true);
            setTimeout(() => {
                window.crestTranslator.start();
                setIsTranslating(false);
            }, 500);
        }
    };

    return (
        <TranslationContext.Provider value={value}>
            {/* NOTE: translationError is intentionally NOT rendered here as a global
                fixed/floating banner — a floating toast that could appear on top of any
                page regardless of context was rejected (translation failures should
                never interrupt the page like that). translationError/clearTranslationError
                remain available via context for any page that wants to render it inline
                in its own content flow instead (matching the ApiErrorBanner "full" card
                pattern used elsewhere in reports/SPECIFIC_ERROR_MESSAGES_PLAN.md). */}
            {isTranslating && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(255,255,255,0.75)',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #e0d0eb',
                        borderTop: '4px solid #7D3F98',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                    }} />
                    <p style={{
                        fontFamily: 'CVS Health Sans, sans-serif',
                        fontSize: '16px',
                        color: '#262626',
                        margin: 0
                    }}>
                        Translation is in progress...
                    </p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}
            {children}
        </TranslationContext.Provider>
    );
};
